// currency — AED-prefixed numeric input with thousands separators.
//
// Formatting is applied on blur rather than on every keystroke so the caret
// never jumps mid-typing, which is the usual bug with live-formatted money
// inputs. The stored value stays a plain number string for the payload.
import React, { useEffect, useState } from "react";
import { InputAdornment, TextField, Typography } from "@mui/material";
import FieldFrame from "./FieldFrame";

const strip = (v) => String(v ?? "").replace(/[^\d.]/g, "");
const group = (v) => {
  const clean = strip(v);
  if (!clean) return "";
  const [whole, fraction] = clean.split(".");
  const grouped = Number(whole).toLocaleString("en-AE");
  return fraction !== undefined ? `${grouped}.${fraction}` : grouped;
};

const CurrencyInput = ({ field, value, error, disabled, onChange, onBlur }) => {
  const id = `f-${field.name}`;
  const [display, setDisplay] = useState(() => group(value));

  // Keep the display in step when the value changes from outside (OCR pre-fill,
  // a reset) without fighting the user while the field has focus.
  useEffect(() => {
    setDisplay((current) => (strip(current) === strip(value) ? current : group(value)));
  }, [value]);

  return (
    <FieldFrame
      label={field.label}
      required={field.required}
      help={field.help}
      error={error}
      htmlFor={id}
    >
      <TextField
        id={id}
        fullWidth
        size="small"
        variant="outlined"
        name={field.name}
        value={display}
        disabled={disabled}
        error={!!error}
        placeholder={field.placeholder || "0"}
        inputProps={{ inputMode: "decimal", "aria-invalid": !!error }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Typography sx={{ color: "#7B6E66", fontWeight: 600, fontSize: "0.875rem" }}>
                {field.props?.currency || "AED"}
              </Typography>
            </InputAdornment>
          ),
        }}
        onChange={(e) => {
          const next = strip(e.target.value);
          setDisplay(e.target.value);
          onChange({ target: { value: next } });
        }}
        onBlur={(e) => {
          setDisplay(group(display));
          if (onBlur) onBlur(e);
        }}
      />
    </FieldFrame>
  );
};

export default CurrencyInput;
